/**
 * Email templates used when sending a reminder (invoice) document to the client
 * from the Reminders page. One template per GrECo company; companies without a
 * dedicated template fall back to the generic English template.
 *
 * Pattern taken over from QTG (frontend/src/library/constants/emailTemplates.ts).
 */

export interface EmailTemplate {
  subject: string;
  body: string;
}

export interface ReminderEmailTemplatePlaceholders {
  Prefix: string; // Salutation prefix (e.g. "Cien." for persons); empty for companies
  ClientName: string; // Client name
  ReminderNumber: string; // Reminder / invoice number
  DueDate: string; // Due date (formatted)
  Amount: string; // Total amount (formatted)
  Currency: string; // Currency code label (e.g. EUR)
  CompanyName: string; // GrECo company name
  ServicePersonName: string; // Logged-in user full name
  ServicePersonPhone: string; // Logged-in user phone
  ServicePersonEMail: string; // Logged-in user email
}

/** GrECo company numbers known to PCT (see backend PCTCommonConstants). */
export const COMPANY_NUMBER_SAGAUTA = 121;
export const COMPANY_NUMBER_GRECO_LV = 137;
export const COMPANY_NUMBER_GRECO_INTERNATIONAL_LT = 148;

/**
 * Replace {Placeholder} tokens in a template string with actual values.
 * Unknown/empty values are replaced with an empty string.
 */
export const replacePlaceholders = (
  template: string,
  data: Partial<ReminderEmailTemplatePlaceholders>
): string => {
  let result = template;
  Object.entries(data).forEach(([key, value]) => {
    const placeholder = `{${key}}`;
    result = result.split(placeholder).join(value || "");
  });
  // collapse doubled spaces that may appear when a prefix is empty
  return result.replace(/>\s{2,}/g, "> ").replace(/\s{2,},/g, ",");
};

export const prepareEmailTemplate = (
  template: EmailTemplate,
  data: Partial<ReminderEmailTemplatePlaceholders>
): EmailTemplate => ({
  subject: replacePlaceholders(template.subject, data),
  body: replacePlaceholders(template.body, data),
});

/**
 * GrECo Latvia (137) — Latvian invoice email.
 * Adapted from QTG MTPL_INVOICE_EMAIL_TEMPLATE: vehicle-specific wording replaced
 * with reminder number / amount / due date.
 */
export const REMINDER_EMAIL_TEMPLATE_GRECO_LV: EmailTemplate = {
  subject: "Rēķins Nr. {ReminderNumber} par apdrošināšanas prēmiju",
  body: `
<h1><span style="font-size: 14pt; color: #000099;">{Prefix} {ClientName},</span></h1>
<p class="MsoNormal" style="margin: 0cm 0cm 0pt;"><span style="font-size: 12pt; font-family: times new roman, serif;">&nbsp;</span></p>
<p class="MsoNormal" style="margin: 0cm 0cm 0pt;"><span style="font-size: 12pt; font-family: times new roman, serif;">Paldies, ka izvēlējāties mūsu apdrošināšanas brokeru sabiedrības pakalpojumus.</span></p>
<p class="MsoNormal" style="margin: 0cm 0cm 0pt;"><span style="font-size: 12pt; font-family: times new roman, serif;">Pielikumā nosūtām rēķinu Nr. <span style="color: #000099;">{ReminderNumber}</span> par apdrošināšanas prēmiju. Kopējā apmaksājamā summa: <b>{Amount} {Currency}</b>. Apmaksas termiņš: <b>{DueDate}</b>.</span></p>
<p class="MsoNormal" style="margin: 0cm 0cm 0pt;"><span style="font-size: 12pt; font-family: times new roman, serif;">&nbsp;</span></p>
<p class="MsoNormal" style="margin: 0cm 0cm 0pt;"><b><span style="font-size: 12pt; font-family: times new roman, serif; color: #000099;">Maksājuma informācija:</span></b></p>
<ul type="disc" style="margin-top: 0cm;">
<li class="MsoNormal" style="margin: 0cm 0cm 0pt;"><span style="font-size: 12pt; font-family: times new roman, serif;">Lūdzam veikt apmaksu saskaņā ar pievienotajā rēķinā norādīto informāciju.</span></li>
<li class="MsoNormal" style="margin: 0cm 0cm 0pt;"><span style="font-size: 12pt; font-family: times new roman, serif;">Ja apmaksa jau ir veikta, lūdzam šo vēstuli neņemt vērā.</span></li>
</ul>
<p class="MsoNormal" style="margin: 0cm 0cm 0pt;"><span style="font-size: 12pt; font-family: times new roman, serif;">&nbsp;</span></p>
<p class="MsoNormal" style="margin: 0cm 0cm 0pt;"><span style="font-size: 12pt; font-family: times new roman, serif;">Informāciju par rēķina saturu saņemsiet sūtot e-pastu uz <b>norekini@greco.services</b> vai zvanot uz tālruni <b>+371 28662279</b>.</span></p>
<p class="MsoNormal" style="margin: 0cm 0cm 0pt;"><span style="font-size: 12pt; font-family: times new roman, serif;">&nbsp;</span></p>
<p class="MsoNormal" style="margin: 0cm 0cm 0pt;"><span style="font-size: 12pt; font-family: times new roman, serif;">Ar cieņu,<br />{ServicePersonName}<br />{CompanyName}</span></p>
<p></p>
  `,
};

/**
 * GrECo Sagauta (121) — Lithuanian translation of the generic template.
 */
export const REMINDER_EMAIL_TEMPLATE_SAGAUTA: EmailTemplate = {
  subject: "Sąskaita Nr. {ReminderNumber} - draudimo įmoka",
  body: `
<h1><span style="font-size: 14pt; color: #000099;">{ClientName},</span></h1>
<p style="margin: 0cm 0cm 0pt;"><span style="font-size: 12pt; font-family: times new roman, serif;">&nbsp;</span></p>
<p style="margin: 0cm 0cm 0pt;"><span style="font-size: 12pt; font-family: times new roman, serif;">Dėkojame, kad pasirinkote mūsų draudimo brokerių paslaugas.</span></p>
<p style="margin: 0cm 0cm 0pt;"><span style="font-size: 12pt; font-family: times new roman, serif;">Priede siunčiame sąskaitą Nr. <span style="color: #000099;">{ReminderNumber}</span> už draudimo įmoką. Bendra mokėtina suma: <b>{Amount} {Currency}</b>. Apmokėjimo terminas: <b>{DueDate}</b>.</span></p>
<p style="margin: 0cm 0cm 0pt;"><span style="font-size: 12pt; font-family: times new roman, serif;">&nbsp;</span></p>
<p style="margin: 0cm 0cm 0pt;"><b><span style="font-size: 12pt; font-family: times new roman, serif; color: #000099;">Mokėjimo informacija:</span></b></p>
<ul type="disc" style="margin-top: 0cm;">
<li style="margin: 0cm 0cm 0pt;"><span style="font-size: 12pt; font-family: times new roman, serif;">Prašome atlikti mokėjimą pagal pridėtoje sąskaitoje nurodytą informaciją.</span></li>
<li style="margin: 0cm 0cm 0pt;"><span style="font-size: 12pt; font-family: times new roman, serif;">Jei mokėjimas jau atliktas, prašome nekreipti dėmesio į šį pranešimą.</span></li>
</ul>
<p style="margin: 0cm 0cm 0pt;"><span style="font-size: 12pt; font-family: times new roman, serif;">&nbsp;</span></p>
<p style="margin: 0cm 0cm 0pt;"><span style="font-size: 12pt; font-family: times new roman, serif;">Kilus klausimams, prašome susisiekti su manimi: <b>{ServicePersonName}</b>, tel. <b>{ServicePersonPhone}</b>, el. paštas <b>{ServicePersonEMail}</b>.</span></p>
<p style="margin: 0cm 0cm 0pt;"><span style="font-size: 12pt; font-family: times new roman, serif;">&nbsp;</span></p>
<p style="margin: 0cm 0cm 0pt;"><span style="font-size: 12pt; font-family: times new roman, serif;">Pagarbiai,<br />{ServicePersonName}<br />{CompanyName}</span></p>
<p></p>
  `,
};

/**
 * Generic English template — used for every GrECo company without a dedicated one
 * (currently 148 GrECo International LT).
 */
export const REMINDER_EMAIL_TEMPLATE_DEFAULT: EmailTemplate = {
  subject: "Invoice No. {ReminderNumber} - insurance premium",
  body: `
<h1><span style="font-size: 14pt; color: #000099;">{ClientName},</span></h1>
<p style="margin: 0cm 0cm 0pt;"><span style="font-size: 12pt; font-family: times new roman, serif;">&nbsp;</span></p>
<p style="margin: 0cm 0cm 0pt;"><span style="font-size: 12pt; font-family: times new roman, serif;">Thank you for choosing our insurance brokerage services.</span></p>
<p style="margin: 0cm 0cm 0pt;"><span style="font-size: 12pt; font-family: times new roman, serif;">Please find attached invoice No. <span style="color: #000099;">{ReminderNumber}</span> for the insurance premium. Total amount due: <b>{Amount} {Currency}</b>. Due date: <b>{DueDate}</b>.</span></p>
<p style="margin: 0cm 0cm 0pt;"><span style="font-size: 12pt; font-family: times new roman, serif;">&nbsp;</span></p>
<p style="margin: 0cm 0cm 0pt;"><b><span style="font-size: 12pt; font-family: times new roman, serif; color: #000099;">Payment information:</span></b></p>
<ul type="disc" style="margin-top: 0cm;">
<li style="margin: 0cm 0cm 0pt;"><span style="font-size: 12pt; font-family: times new roman, serif;">Please make the payment according to the details stated in the attached invoice.</span></li>
<li style="margin: 0cm 0cm 0pt;"><span style="font-size: 12pt; font-family: times new roman, serif;">If the payment has already been made, please disregard this message.</span></li>
</ul>
<p style="margin: 0cm 0cm 0pt;"><span style="font-size: 12pt; font-family: times new roman, serif;">&nbsp;</span></p>
<p style="margin: 0cm 0cm 0pt;"><span style="font-size: 12pt; font-family: times new roman, serif;">Should you have any questions, please contact me: <b>{ServicePersonName}</b>, phone <b>{ServicePersonPhone}</b>, e-mail <b>{ServicePersonEMail}</b>.</span></p>
<p style="margin: 0cm 0cm 0pt;"><span style="font-size: 12pt; font-family: times new roman, serif;">&nbsp;</span></p>
<p style="margin: 0cm 0cm 0pt;"><span style="font-size: 12pt; font-family: times new roman, serif;">Kind regards,<br />{ServicePersonName}<br />{CompanyName}</span></p>
<p></p>
  `,
};

/** Template registry keyed by GrECo company number. */
export const REMINDER_EMAIL_TEMPLATES_BY_COMPANY: Record<number, EmailTemplate> =
  {
    [COMPANY_NUMBER_SAGAUTA]: REMINDER_EMAIL_TEMPLATE_SAGAUTA,
    [COMPANY_NUMBER_GRECO_LV]: REMINDER_EMAIL_TEMPLATE_GRECO_LV,
  };

export const getReminderEmailTemplate = (
  grecoCompanyNumber?: number | null
): EmailTemplate =>
  (grecoCompanyNumber != null &&
    REMINDER_EMAIL_TEMPLATES_BY_COMPANY[grecoCompanyNumber]) ||
  REMINDER_EMAIL_TEMPLATE_DEFAULT;
